import { Component, OnDestroy, inject } from '@angular/core';
import { TableConfig } from '../../shared/components/app-table/app-table-models';
import { Antibiotika, Fynd } from '../models/analys-resultat';
import { AtgardsListaService } from './atgards-lista.service';
import { NameType } from '../../shared/models/models';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { HistorikDialogComponent } from './historik-dialog/historik-dialog.component';
import { Subject, takeUntil } from 'rxjs';
import { Oversikt } from '../../shared/models/manuell-atgard';
import { TableLazyLoadEvent } from 'primeng/table';
import { TableDataService } from 'src/app/shared/services/table-data.service';

@Component({
    selector: 'app-atgards-lista',
    templateUrl: './atgards-lista.component.html',
    styleUrls: ['./atgards-lista.component.scss'],
    standalone: false
})
export class AtgardsListaComponent implements OnDestroy {
  public tableConfig: TableConfig = {
    dataKey: 'rowidentifier',
    headerCaption: 'Provdata',
    accordionContent: 'atgards-lista',
    multiSortMeta: [
      { field: 'type', order: -1 },
      { field: 'name', order: -1 },
      { field: 'labCode', order: -1 },
    ],
  };
  protected loading = true;

  private destroy$ = new Subject<void>();
  private ref: DynamicDialogRef | null = null;
  protected originalData!: any;
  protected paginationLength?: number = undefined;


  private tableDataService = inject(TableDataService);
  private atgardsService = inject(AtgardsListaService);
  private dialogService = inject(DialogService);

  ngOnInit(): void {
    this.atgardsService.refetchAtgarder.pipe(takeUntil(this.destroy$)).subscribe(() => this.getResult());
    this.getResult();
  }

  private getOkantNamn(item: Oversikt, header: NameType, fynd?: Fynd, antibiotic?: Antibiotika) {
    if (item.type === header) {
      return item.name;
    }
    if (header === NameType.Analys) {
      return item.details.analysName;
    }
    if (header === NameType.Provmaterial) {
      return item.details.provdata.provmaterialName;
    }
    if (header === NameType.Smittamne) {
      return fynd?.smittamneName;
    }
    if (header === NameType.Antibiotika) {
      return antibiotic?.name;
    }
    return 'Invalid type';
  }

  sort(e: TableLazyLoadEvent) {
    if (!this.originalData) return;
    this.tableConfig.rows = this.tableDataService.filterSort(e, this.originalData).result;
    this.paginationLength = this.tableDataService.filterSort(e, this.originalData).paginationLength;
  }

  private getOversiktRowByName(oversiktRow: Oversikt, header: NameType, fynd?: Fynd, antibiotic?: Antibiotika) {
    const name = this.getOkantNamn(oversiktRow, header, fynd, antibiotic);
    const selectedRow = this.tableConfig.rows?.find(
      (row) => row['name'] === name && row['labCode'] && oversiktRow.labCode,
    );

    if (!selectedRow) throw new Error('Could not find row with that name and labCode');

    return selectedRow;
  }

  private showCommentHistorik(
    oversiktRow: Oversikt,
    header: NameType,
    fynd?: Fynd,
    antibiotic?: Antibiotika,
  ) {
    const dialogRef = this.dialogService.open(HistorikDialogComponent, {
      header: `Historik`,
      // header: `Åtgärda ${this.getAtgardsDialogTitle(atgardItem.namntyp)}`,
      width: '',
      draggable: true,
      contentStyle: { overflow: 'auto' },
      baseZIndex: 10000,
      maximizable: false,
      data: {
        atgard: this.getOversiktRowByName(oversiktRow, header, fynd, antibiotic),
      },
    });

    if (!dialogRef) {
      return;
    }

    this.ref = dialogRef;

    dialogRef.onClose.subscribe(() => {
      this.getResult();
    });
  }
  private async getResult() {
    this.atgardsService.getAtgarder().subscribe((data: any) => {

      this.originalData = data;
      this.loading = false;

      this.sort({
        first: 0,
        filters: {},
        multiSortMeta: this.tableConfig.multiSortMeta
      })
    });

    this.tableConfig.columns = [
      {
        field: 'toggler',
        header: '',
        isRowToggler: true,
      },
      {
        field: 'name',
        header: 'Okänt namn',
        sortByField: 'name',
        columnFilter: 'text',
      },
      {
        field: 'type',
        header: 'Typ av lista',
        sortByField: 'type',
        columnFilter: 'multi-select',
        filterOptions: [
          { name: 'ANALYS', label: 'Analys' },
          { name: 'PROVMATERIAL', label: 'Provmaterial' },
          { name: 'SMITTAMNE', label: 'Smittämne' },
          { name: 'ANTIBIOTIKUM', label: 'Antibiotikum' },
        ],
      },
      {
        field: 'labCode',
        header: 'Labbkod',
        sortByField: 'labCode',
        columnFilter: 'multi-select',
      },
      {
        field: 'num',
        header: 'Antal prov med okänt namn',
        sortByField: 'num',
        columnFilter: 'text',
      },
      {
        field: 'provId',
        header: 'Prov ID',
        sortByField: 'provId',
        columnFilter: 'text',
      },
    ];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
